// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Wraps `GET /api/v1/sop-factory/artifacts` (`ENDPOINTS.SOP_FACTORY_ARTIFACTS`
 * in `utils/constants.ts`) — declared in main but not called from any main
 * service yet (`LibraryPage.tsx` is still a static mockup), so this is the
 * first caller, not a copy of one. Lives in `shared/` so admin-center never
 * imports `services/`/`utils/` directly (ADR-0014). The fetch wrapper itself
 * (`get`) stays imported from the real `services/api` — it carries token
 * attach + 401/session-expiry handling that must stay single-sourced, not
 * duplicated.
 */

import { get, post, put } from '../../helios/services/api';
import { ENDPOINTS } from '../utils/constants';
import type { Artifact, PagedArtifacts } from '../types/procedures';

export interface ListArtifactsParams {
  status?: string;
  domain?: string;
  artifact_type?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export const listArtifacts = async (params: ListArtifactsParams = {}): Promise<PagedArtifacts> => {
  const queryParams = new URLSearchParams();
  if (params.status !== undefined) queryParams.append('status', params.status);
  if (params.domain !== undefined) queryParams.append('domain', params.domain);
  if (params.artifact_type !== undefined) queryParams.append('artifact_type', params.artifact_type);
  if (params.q !== undefined) queryParams.append('q', params.q);
  if (params.page !== undefined) queryParams.append('page', String(params.page));
  if (params.page_size !== undefined) queryParams.append('page_size', String(params.page_size));

  const url = queryParams.toString()
    ? `${ENDPOINTS.SOP_FACTORY_ARTIFACTS}?${queryParams}`
    : ENDPOINTS.SOP_FACTORY_ARTIFACTS;
  return get<PagedArtifacts>(url);
};

/**
 * With no `version`, `GET .../artifacts/{id}` returns the latest *approved*
 * version only, so a draft, in-review or deprecated artifact 404s even though
 * the library lists it (ADO #33607). With a `version`, the exact version the
 * caller picked is fetched from `.../versions/{version}`, which serves any
 * status.
 */
export const getArtifact = async (artifactId: string, version?: string): Promise<Artifact> => {
  const base = `${ENDPOINTS.SOP_FACTORY_ARTIFACTS}/${encodeURIComponent(artifactId)}`;
  return get<Artifact>(version ? `${base}/versions/${encodeURIComponent(version)}` : base);
};

/**
 * No backend route exists for this yet — SOP Factory is currently
 * read-only (see `back/app/routers/sop_factory.py`). This is the
 * anticipated shape (`PUT .../artifacts/{id}`, matching the full-resource
 * update convention `back/app/routers/users.py` already uses), written so
 * the only remaining step once the route exists is confirming the path/verb
 * match — no frontend change needed.
 */
export const updateArtifact = async (artifactId: string, artifact: Artifact): Promise<Artifact> =>
  put<Artifact>(`${ENDPOINTS.SOP_FACTORY_ARTIFACTS}/${encodeURIComponent(artifactId)}`, artifact);

/**
 * No backend route exists for this yet either (same situation as
 * `updateArtifact` above). Placeholder URL: `POST` to the same collection
 * endpoint `listArtifacts` reads from — the standard REST convention for
 * "create" on a collection resource. If SOP Factory's real create route
 * ends up different, this one line (the `ENDPOINTS.SOP_FACTORY_ARTIFACTS`
 * argument to `post`) is the only thing that needs to change.
 */
export const createArtifact = async (artifact: Artifact): Promise<Artifact> =>
  post<Artifact>(ENDPOINTS.SOP_FACTORY_ARTIFACTS, artifact);
